import {
  AgeGroup,
  getBalltrackerStrikeZoneBySportOptions,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export type StrikeZonePresetView = {
  label: AgeGroup;
  name: string;
  bottom: number;
  top: number;
  left: number;
  right: number;
  strikeZoneYInches: number;
  medianHeight: string;
  defaultExpansionDown: number;
  defaultExpansionUp: number;
  defaultExpansionSide: number;
};

type PresetUIMeta = Pick<
  StrikeZonePresetView,
  | "medianHeight"
  | "defaultExpansionDown"
  | "defaultExpansionUp"
  | "defaultExpansionSide"
>;

// UI-only metadata not returned by the balltracker API: cosmetic label
// (median hitter height) and the default expansion slider values when a
// preset is first picked. Keyed by sport, then by age-group label.
const BASEBALL_PRESET_UI_META: Record<string, PresetUIMeta> = {
  "8U": {
    medianHeight: "4'4\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "9U": {
    medianHeight: "4'6\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "10U": {
    medianHeight: "4'8\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "11U": {
    medianHeight: "4'11\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "12U": {
    medianHeight: "5'3\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "13U": {
    medianHeight: "5'7\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 2,
    defaultExpansionSide: 2,
  },
  "14U": {
    medianHeight: "5'9\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 2,
    defaultExpansionSide: 2,
  },
  "15U": {
    medianHeight: "5'11\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "16U": {
    medianHeight: "6'0\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 1.5,
  },
  "17U": {
    medianHeight: "6'0\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 1,
    defaultExpansionSide: 1,
  },
  "18U": {
    medianHeight: "6'1\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 1,
    defaultExpansionSide: 1,
  },
  College: {
    medianHeight: "6'1\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 0,
    defaultExpansionSide: 0,
  },
  Pro: {
    medianHeight: "6'2\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 0,
    defaultExpansionSide: 0,
  },
};

const SOFTBALL_PRESET_UI_META: Record<string, PresetUIMeta> = {
  "8U": {
    medianHeight: "4'3\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "9U": {
    medianHeight: "4'5\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "10U": {
    medianHeight: "4'7\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "11U": {
    medianHeight: "4'10\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "12U": {
    medianHeight: "5'1\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1.5,
    defaultExpansionSide: 2,
  },
  "13U": {
    medianHeight: "5'3\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1,
    defaultExpansionSide: 2,
  },
  "14U": {
    medianHeight: "5'5\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1,
    defaultExpansionSide: 2,
  },
  "15U": {
    medianHeight: "5'6\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1,
    defaultExpansionSide: 1.5,
  },
  "16U": {
    medianHeight: "5'7\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1,
    defaultExpansionSide: 1.5,
  },
  "17U": {
    medianHeight: "5'7\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1,
    defaultExpansionSide: 1,
  },
  "18U": {
    medianHeight: "5'7\"",
    defaultExpansionDown: 0.5,
    defaultExpansionUp: 1,
    defaultExpansionSide: 1,
  },
  College: {
    medianHeight: "5'8\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 0,
    defaultExpansionSide: 0,
  },
  Pro: {
    medianHeight: "5'8\"",
    defaultExpansionDown: 0,
    defaultExpansionUp: 0,
    defaultExpansionSide: 0,
  },
};

const FALLBACK_UI_META: PresetUIMeta = {
  medianHeight: "",
  defaultExpansionDown: 0,
  defaultExpansionUp: 0,
  defaultExpansionSide: 0,
};

const getPresetUIMeta = (sport: Sport): Record<string, PresetUIMeta> => {
  return sport === "softball"
    ? SOFTBALL_PRESET_UI_META
    : BASEBALL_PRESET_UI_META;
};

export type Sport = "baseball" | "softball";

export const useStrikeZonePresets = (sport: Sport) => {
  const { data, isLoading, error } = useQuery(
    getBalltrackerStrikeZoneBySportOptions({ path: { sport } }),
  );

  const presets = useMemo<StrikeZonePresetView[]>(() => {
    if (!data) return [];
    const uiMeta = getPresetUIMeta(sport);
    return Object.entries(data).map(([label, preset]) => ({
      label: label as AgeGroup,
      name: preset.name,
      bottom: preset.bottomInches,
      top: preset.topInches,
      left: -preset.sideFromCenterInches,
      right: preset.sideFromCenterInches,
      strikeZoneYInches: preset.strikeZoneYInches,
      ...(uiMeta[label] ?? FALLBACK_UI_META),
    }));
  }, [data, sport]);

  return { presets, isLoading, error };
};

/**
 * Match a saved strike-zone `name` to one of the loaded presets.
 *
 * Handles both the new API format (e.g. "baseball - 9U - base") and the
 * legacy frontend-generated format (e.g. "age9 - baseball", "college - baseball")
 * so existing saved configs continue to hydrate.
 */
export const findPresetByName = (
  name: string,
  presets: StrikeZonePresetView[],
): StrikeZonePresetView | undefined => {
  if (!name || presets.length === 0) return undefined;

  const direct = presets.find((p) => name.startsWith(p.name));
  if (direct) return direct;

  // Legacy: "age{N} - {sport}..." → label "{N}U"
  const ageMatch = name.match(/^age(\d+)\b/i);
  if (ageMatch) {
    const label = `${ageMatch[1]}U`;
    const byLabel = presets.find((p) => p.label === label);
    if (byLabel) return byLabel;
  }

  // Legacy: "college - {sport}..." or "pro - {sport}..." → label "College" / "Pro"
  const keyword = name.match(/^(college|pro)\b/i);
  if (keyword) {
    const label =
      keyword[1].charAt(0).toUpperCase() + keyword[1].slice(1).toLowerCase();
    const byLabel = presets.find((p) => p.label === label);
    if (byLabel) return byLabel;
  }

  return undefined;
};
